/**
 * Entity Link Service
 *
 * Single source of truth for "given an entity, produce its shareable URL(s)".
 *
 * Rules:
 * - /entity/:id  is the canonical shareable URL for every entity type.
 * - Type-specific aliases (e.g. /docs/:id, /automations/:id) are nicer but
 *   semantically identical — they all render StandaloneEntityView.
 * - Docs keep /docs/:id as their canonical alias to preserve existing bookmarks,
 *   SEO, and the dedicated DocumentAccessWrapper flow.
 * - getShareUrl never forces a redirect into a Space; the caller decides whether
 *   to also show an "Open in space" link via getInSpaceUrl.
 *
 * NOTE: The Python equivalent lives in backend/services/entity_link_service.py.
 * Keep aliases in sync; backend/tests/test_entity_link_service.py catches drift.
 */

import { createFileLogger } from '../utils/logger';
import { getCapability } from '../utils/entityLinkCapabilities';

const log = createFileLogger('ENTITY_LINK', { enabled: true });

export interface EntityLinkOptions {
  /** Include the full origin (https://rivereditor.com) — use for email bodies. */
  absolute?: boolean;
  /** Reserved for future signed/time-limited link tokens. Not yet implemented. */
  token?: string;
}

export interface EntityUrls {
  /** Canonical share URL — use this everywhere (clipboard, emails, OG). */
  share: string;
  /** Always /entity/:id regardless of type-specific alias. */
  standalone: string;
  /**
   * In-space URL when entity has a spaceId. Null for entities that live at the
   * dataspace level (Automations, Tasks, etc.) or when spaceId is unknown.
   * This is NOT the share URL — it's provided so UIs can offer an "Open in space" option.
   */
  inSpace: string | null;
}

function getOrigin(absolute?: boolean): string {
  if (!absolute) return '';
  return typeof window !== 'undefined' ? window.location.origin : 'https://rivereditor.com';
}

/**
 * Core URL builder. Returns the canonical standalone URL for any entity.
 */
function buildShareUrl(entity: { _id?: string; id?: string; entityType?: string; type?: string }, opts?: EntityLinkOptions): string {
  const id = entity._id || entity.id || '';
  const entityType = entity.entityType || entity.type || '';
  const origin = getOrigin(opts?.absolute);

  if (!id) {
    log.warn('[entityLinkService] getShareUrl called with no entity id', { entity });
    return `${origin}/entity/unknown`;
  }

  const cap = getCapability(entityType);

  // Docs keep their dedicated /docs/ route to preserve existing links + DocumentAccessWrapper
  if (entityType === 'Doc') {
    const url = `${origin}/docs/${id}`;
    log.info('[entityLinkService] getShareUrl (Doc alias)', { id, entityType, url });
    return url;
  }

  // Use per-type alias when configured (e.g. /automations/:id)
  if (cap.standaloneAlias) {
    const url = `${origin}${cap.standaloneAlias}/${id}`;
    log.info('[entityLinkService] getShareUrl (type alias)', { id, entityType, alias: cap.standaloneAlias, url });
    return url;
  }

  // Canonical /entity/:id fallback
  const url = `${origin}/entity/${id}`;
  log.info('[entityLinkService] getShareUrl (canonical)', { id, entityType, url });
  return url;
}

/**
 * Get the standalone URL (always /entity/:id, no type-specific alias).
 * Useful when you need a single URL format regardless of entity type.
 */
function buildStandaloneUrl(entity: { _id?: string; id?: string }, opts?: EntityLinkOptions): string {
  const id = entity._id || entity.id || '';
  const origin = getOrigin(opts?.absolute);
  return `${origin}/entity/${id}`;
}

/**
 * Get an in-space URL when the entity has a spaceId.
 * Returns null for entities that don't belong to a specific space.
 * This URL opens the entity inside its Space window system.
 */
function buildInSpaceUrl(entity: { _id?: string; id?: string; spaceId?: string }, opts?: EntityLinkOptions): string | null {
  const entityId = entity._id || entity.id;
  const spaceId = (entity as any).spaceId || (entity as any).skeleton?.spaceId;
  if (!entityId || !spaceId) return null;
  const origin = getOrigin(opts?.absolute);
  return `${origin}/spaces/${spaceId}?entity=${entityId}`;
}

export const entityLinkService = {
  /**
   * Get the canonical share URL for an entity.
   * This is what should be put on the clipboard, in emails, and in OG tags.
   */
  getShareUrl(entity: any, opts?: EntityLinkOptions): string {
    return buildShareUrl(entity, opts);
  },

  /**
   * Get the /entity/:id URL (always, no alias substitution).
   */
  getStandaloneUrl(entity: any, opts?: EntityLinkOptions): string {
    return buildStandaloneUrl(entity, opts);
  },

  /**
   * Get the in-space URL (for entities that have a spaceId).
   * Returns null when the entity is not space-bound.
   */
  getInSpaceUrl(entity: any, opts?: EntityLinkOptions): string | null {
    return buildInSpaceUrl(entity, opts);
  },

  /**
   * Get all URLs in one call.
   */
  getAllUrls(entity: any, opts?: EntityLinkOptions): EntityUrls {
    return {
      share: buildShareUrl(entity, opts),
      standalone: buildStandaloneUrl(entity, opts),
      inSpace: buildInSpaceUrl(entity, opts),
    };
  },
};

// ---- Dev-only console helper ----
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__entityLink = (entityOrId: any, opts?: EntityLinkOptions) => {
    // Accept either a full entity object or just an ID string
    const entity = typeof entityOrId === 'string' ? { _id: entityOrId } : entityOrId;
    const urls = entityLinkService.getAllUrls(entity, opts);
    console.table(urls);
    return urls;
  };
}
